import { useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  CreditCard,
  Smartphone,
  Banknote,
  ShieldCheck,
  AlertCircle,
  CheckCircle,
  RotateCcw,
  Clock,
} from "lucide-react";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  usePaymentKPIs,
  usePaymentTransactions,
  useConfirmPayment,
  useRefundPayment,
} from "@/hooks/admin/usePayments";
import { useModuleEnabled } from "@/hooks/admin/useModules";
import type {
  PaymentTransaction,
  PaymentMethod,
  PaymentStatus,
} from "@/lib/admin-api/payment";
import {
  getMethodLabel,
  getMethodClass,
  getStatusLabel,
  getStatusClass,
} from "./constants";
import { formatFcfa, formatDate } from "./formatters";
import { ActionModal } from "./ActionModal";

export function TransactionsTab() {
  const [search, setSearch] = useState("");
  const [method, setMethod] = useState<PaymentMethod | "">("");
  const [status, setStatus] = useState<PaymentStatus | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [actionTarget, setActionTarget] = useState<{
    id: string;
    ref: string;
    type: "confirm" | "refund";
    amount: number;
  } | null>(null);

  // S3-H5: Refund button gated on the returns-refunds module flag
  const returnsRefundsEnabled = useModuleEnabled("returns-refunds");

  const params = useMemo(
    () => ({
      q: search || null,
      method: method || null,
      status: status || null,
      date_from: dateFrom || null,
      date_to: dateTo || null,
    }),
    [search, method, status, dateFrom, dateTo],
  );

  const { data: kpisData, isLoading: kpisLoading } = usePaymentKPIs();
  const { data, isLoading, isError, refetch } = usePaymentTransactions(params);
  const confirmPayment = useConfirmPayment();
  const refundPayment = useRefundPayment();

  const payments = data?.payments ?? [];

  // Global pending count comes from the KPI endpoint so it reflects the whole
  // table, not just the current page. Fall back to the loaded page if the KPI
  // field is missing (e.g., stale server).
  const pendingCount =
    kpisData?.pending_count ??
    payments.filter((p) => p.status === "pending").length;

  const handleAction = useCallback(() => {
    if (!actionTarget) return;
    const mutation = actionTarget.type === "confirm" ? confirmPayment : refundPayment;
    const target = actionTarget; // snapshot for closure
    mutation.mutate(actionTarget.id, {
      // Phase-3 QA: the modal previously only closed on success and
      // stayed stuck when the hook-level onSuccess threw (e.g. toast
      // failed). Using onSettled guarantees we clear modal state
      // whichever branch hits. Errors still surface via toast from
      // the mutation hook itself.
      onSettled: () => setActionTarget(null),
      onSuccess: () => {
        if (target.type === "confirm") {
          toast.success(`Paiement ${target.ref} confirmé`);
        } else {
          toast.success(`Remboursement initié pour ${target.ref}`);
        }
      },
      onError: (err) => {
        toast.error(
          err instanceof Error
            ? err.message
            : `Échec de l'opération sur ${target.ref}`,
        );
      },
    });
  }, [actionTarget, confirmPayment, refundPayment]);

  const columns: readonly ColumnDef<PaymentTransaction>[] = useMemo(
    () => [
      {
        id: "ref",
        header: "Référence",
        cell: (t) => (
          <span className="font-mono text-xs font-medium text-stone-900">
            {t.ref || "—"}
          </span>
        ),
      },
      {
        id: "method",
        header: "Méthode",
        cell: (t) => (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${getMethodClass(t.method)}`}
          >
            {getMethodLabel(t.method)}
          </span>
        ),
      },
      {
        id: "amount_fcfa",
        header: "Montant",
        cell: (t) => (
          <span className="font-medium text-stone-900">
            {formatFcfa(t.amount_fcfa)}
          </span>
        ),
      },
      {
        id: "fee_fcfa",
        header: "Frais",
        cell: (t) => (
          <span className="text-sm text-stone-500">{formatFcfa(t.fee_fcfa)}</span>
        ),
      },
      {
        id: "net_fcfa",
        header: "Net",
        cell: (t) => (
          <span className="text-sm font-medium text-emerald-700">
            {formatFcfa(t.net_fcfa)}
          </span>
        ),
      },
      {
        id: "customer_name",
        header: "Client",
        cell: (t) => (
          <div>
            <div className="text-sm font-medium text-stone-900">
              {t.customer_name ?? "—"}
            </div>
            {t.customer_phone && (
              <div className="text-xs text-stone-500">{t.customer_phone}</div>
            )}
          </div>
        ),
      },
      {
        id: "order_ref",
        header: "Commande",
        cell: (t) => (
          <span className="font-mono text-xs text-stone-600">
            {t.order_ref ?? "—"}
          </span>
        ),
      },
      {
        id: "created_at",
        header: "Date",
        cell: (t) => (
          <span className="text-sm text-stone-500">{formatDate(t.created_at)}</span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (t) => (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusClass(t.status)}`}
          >
            {getStatusLabel(t.status)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (t) => (
          <div className="flex items-center gap-1">
            {t.status === "pending" && (
              <button
                type="button"
                onClick={() =>
                  setActionTarget({
                    id: t.id,
                    ref: t.ref,
                    type: "confirm",
                    amount: t.amount_fcfa,
                  })
                }
                title="Confirmer le paiement"
                className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Confirmer
              </button>
            )}
            {t.status === "confirmed" && returnsRefundsEnabled && (
              <button
                type="button"
                onClick={() =>
                  setActionTarget({
                    id: t.id,
                    ref: t.ref,
                    type: "refund",
                    amount: t.amount_fcfa,
                  })
                }
                title="Initier un remboursement"
                className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Rembourser
              </button>
            )}
          </div>
        ),
      },
    ],
    [],
  );

  const kpis = kpisData;

  const clearDateFilters = useCallback(() => {
    setDateFrom("");
    setDateTo("");
  }, []);

  return (
    <div className="space-y-5">
      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <KPICard
          label="Volume total"
          value={kpisLoading ? 0 : (kpis?.total_volume_fcfa ?? 0)}
          format="currency"
          icon={<CreditCard className="h-4 w-4" />}
        />
        <KPICard
          label="En attente"
          value={pendingCount}
          format="number"
          icon={<Clock className="h-4 w-4" />}
        />
        <KPICard
          label="Wave"
          value={kpisLoading ? 0 : (kpis?.wave_volume_fcfa ?? 0)}
          format="currency"
          icon={<Smartphone className="h-4 w-4" />}
        />
        <KPICard
          label="Orange Money"
          value={kpisLoading ? 0 : (kpis?.orange_money_volume_fcfa ?? 0)}
          format="currency"
          icon={<Smartphone className="h-4 w-4" />}
        />
        <KPICard
          label="Espèces / COD"
          value={kpisLoading ? 0 : (kpis?.cod_volume_fcfa ?? 0)}
          format="currency"
          icon={<Banknote className="h-4 w-4" />}
        />
        <KPICard
          label="Taux de succès"
          value={kpisLoading ? 0 : (kpis?.success_rate ?? 0)}
          format="percent"
          icon={<ShieldCheck className="h-4 w-4" />}
        />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full sm:w-64">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Rechercher ref, client…"
          />
        </div>
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value as PaymentMethod | "")}
          className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
        >
          <option value="">Toutes les méthodes</option>
          <option value="wave">Wave</option>
          <option value="orange_money">Orange Money</option>
          <option value="cash_on_delivery">Espèces / COD</option>
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as PaymentStatus | "")}
          className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
        >
          <option value="">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="confirmed">Confirmé</option>
          <option value="failed">Échoué</option>
        </select>

        {/* Date range */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-stone-600">Du :</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-stone-600">Au :</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        {(dateFrom || dateTo) && (
          <button
            type="button"
            onClick={clearDateFilters}
            className="text-sm text-stone-500 hover:text-stone-700"
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Error state */}
      {isError && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Impossible de charger les transactions.</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
          >
            Réessayer
          </button>
        </div>
      )}

      {!isError && !isLoading && payments.length === 0 ? (
        <EmptyState
          icon={<CreditCard className="h-6 w-6" />}
          title="Aucune transaction"
          description="Aucune transaction ne correspond aux filtres sélectionnés."
        />
      ) : (
        <AdminDataTable
          data={payments}
          columns={columns}
          isLoading={isLoading}
        />
      )}

      {/* Action confirmation modal */}
      <ActionModal
        open={actionTarget !== null}
        title={
          actionTarget?.type === "confirm"
            ? "Confirmer le paiement"
            : "Initier un remboursement"
        }
        description={
          actionTarget?.type === "confirm"
            ? `Confirmer le paiement ${actionTarget.ref} de ${formatFcfa(actionTarget.amount)} ?`
            : `Initier le remboursement de ${actionTarget ? formatFcfa(actionTarget.amount) : ""} pour le paiement ${actionTarget?.ref ?? ""} ?`
        }
        confirmLabel={
          actionTarget?.type === "confirm" ? "Confirmer" : "Rembourser"
        }
        confirmClassName={
          actionTarget?.type === "refund"
            ? "bg-amber-600 hover:bg-amber-700"
            : undefined
        }
        onClose={() => setActionTarget(null)}
        onConfirm={handleAction}
        isPending={confirmPayment.isPending || refundPayment.isPending}
      />
    </div>
  );
}
